import { useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField } from '../components/Field.jsx';
import RowActions from '../components/RowActions.jsx';
import { Loader, ErrorAlert, EmptyRow, StatusBadge } from '../components/States.jsx';

import useForm from '../hooks/useForm.js';
import {
  useListCatalogQuery,
  useCreateCatalogMutation,
  useUpdateCatalogMutation,
  useToggleCatalogMutation,
  useDeleteCatalogMutation,
} from '../store/apiSlice.js';
import { catalogSchema } from '../validation/schemas.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import { fmtDate } from '../utils/format.js';
import { downloadCsv } from '../utils/csv.js';

/** Powers all four catalogs: Product Name, Texture, Length, Color. */
export default function CatalogPage({ kind, eyebrow, title, subtitle, label, placeholder }) {
  const showToast = useToast();
  const { reload: reloadReferenceData } = useReferenceData();
  const { data: rows, isLoading: loading, error: apiError, refetch: reload } = useListCatalogQuery({ kind });
  const error = apiError?.message ?? null;

  const [createCatalog] = useCreateCatalogMutation();
  const [updateCatalog] = useUpdateCatalogMutation();
  const [toggleCatalog] = useToggleCatalogMutation();
  const [deleteCatalog] = useDeleteCatalogMutation();

  const form = useForm({ name: '' }, catalogSchema);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const cancelEdit = () => {
    setEditingId(null);
    form.reset({ name: '' });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.validate()) return;

    setBusy(true);
    try {
      if (editingId) await updateCatalog({ kind, id: editingId, payload: form.values }).unwrap();
      else await createCatalog({ kind, payload: form.values }).unwrap();

      showToast(editingId ? `${label} updated` : `${label} added`);
      cancelEdit();
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      // A 409 duplicate belongs on the input, not only in a toast.
      if (!form.applyServerErrors(err.details)) showToast(err.message, 'error');
      else showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    form.fill({ name: row.name });
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    setBusy(true);
    try {
      await deleteCatalog({ kind, id: row.id }).unwrap();
      if (editingId === row.id) cancelEdit();
      showToast(`${label} deleted`);
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (row) => {
    setBusy(true);
    try {
      const updated = await toggleCatalog({ kind, id: row.id }).unwrap();
      showToast(updated.status === 'disabled' ? `${label} disabled` : `${label} enabled`);
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    downloadCsv(`${kind}-list`, [
      { label: 'Sr. No', value: (row) => rows.indexOf(row) + 1 },
      { label, value: (row) => row.name },
      { label: 'Status', value: (row) => row.status || 'active' },
      { label: 'Created At', value: (row) => row.createdAt || '' },
      { label: 'Last Updated', value: (row) => row.updatedAt || '' },
    ], rows || []);
  };

  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />

      <Card title={editingId ? `Edit ${label}` : `Add ${label}`} tag={label}>
        {editingId && (
          <div className="editing-bar">
            <span>Editing “{form.values.name}”</span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField
              label={label}
              placeholder={placeholder}
              id="catalog-name"
              maxLength={60}
              {...form.fieldProps('name')}
            />
          </div>
          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={busy}>
              {editingId ? `Update ${label}` : 'Add'}
            </button>
          </div>
        </form>
      </Card>

      <Card title={`${label} List`} tag={`All ${label}s`}>
        <div className="table-toolbar">
          <button type="button" className="btn ghost" onClick={exportCsv} disabled={!rows?.length}>
            Export CSV
          </button>
        </div>
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="list-table">
              <thead>
                <tr>
                  <th className="sr-col">Sr. No</th>
                  <th>{label}</th>
                  <th>Last Updated</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {!rows?.length ? (
                  <EmptyRow colSpan={4}>Add your first {label.toLowerCase()} above</EmptyRow>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id} className={row.status === 'disabled' ? 'row-disabled' : ''}>
                      <td className="sr-col">{index + 1}</td>
                      <td>
                        {row.name} <StatusBadge status={row.status} />
                      </td>
                      <td className="nowrap">{fmtDate(row.updatedAt)}</td>
                      <td className="action-col">
                        <RowActions
                          status={row.status}
                          busy={busy}
                          onEdit={() => startEdit(row)}
                          onDelete={() => handleDelete(row)}
                          onToggle={() => handleToggle(row)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
